import type { CVDocument } from '@/hooks/use-cv-upload'

export type TalentProfilePreview = {
  id: string
  full_name?: string | null
  headline?: string | null
  city?: string | null
  country?: string | null
  skills?: string[]
  top_skills: string[]
  /** Distinct employer names from experience (people browse facets / company filter). */
  experience_companies?: string[]
  recent_role_title?: string | null
  recent_role_company?: string | null
  skill_count: number
  experience_count: number
  open_to_work?: boolean
}

export function talentDisplayName(
  doc: Pick<CVDocument, 'parsing_status' | 'profile_preview'>,
): string {
  const name = doc.profile_preview?.full_name?.trim()
  if (name) return name
  if (doc.parsing_status === 'failed') return 'Parse failed'
  if (doc.parsing_status === 'pending' || doc.parsing_status === 'processing') {
    return 'Parsing profile…'
  }
  return 'Unknown profile'
}

export function talentLocationLine(preview?: TalentProfilePreview | null): string | null {
  if (!preview) return null
  const parts = [preview.city, preview.country].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : null
}

export function talentRoleLine(preview?: TalentProfilePreview | null): string | null {
  if (!preview) return null
  if (preview.headline?.trim()) return preview.headline.trim()
  if (preview.recent_role_title && preview.recent_role_company) {
    return `${preview.recent_role_title} @ ${preview.recent_role_company}`
  }
  if (preview.recent_role_title) return preview.recent_role_title
  if (preview.recent_role_company) return preview.recent_role_company
  return null
}

export function talentMetadataLine(preview?: TalentProfilePreview | null): string | null {
  if (!preview) return null
  const parts: string[] = []
  if (preview.skill_count > 0) {
    parts.push(`${preview.skill_count} ${preview.skill_count === 1 ? 'skill' : 'skills'}`)
  }
  if (preview.experience_count > 0) {
    parts.push(`${preview.experience_count} ${preview.experience_count === 1 ? 'role' : 'roles'}`)
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

export function talentAllSkills(preview?: TalentProfilePreview | null): string[] {
  if (!preview) return []
  if (preview.skills?.length) return preview.skills
  return preview.top_skills ?? []
}

export function talentTopSkills(preview?: TalentProfilePreview | null, max = 5): string[] {
  return talentAllSkills(preview).slice(0, max)
}

export function savedCandidateDisplayName(
  candidate: Pick<{ candidate_name: string; profile_preview?: TalentProfilePreview | null }, 'candidate_name'> & {
    profile_preview?: TalentProfilePreview | null
  },
): string {
  return candidate.profile_preview?.full_name?.trim() || candidate.candidate_name
}
